/**
 * career-app — the shell (layout), DNA-aligned dark chrome (charcoal + amber).
 * ONE chrome palette across the whole frame — top bar, left pane, right pane and
 * the footer pipeline bar all read as the same warm-charcoal surface with the amber
 * accent; only the content (<kit-outlet>) follows the light/dark theme.
 *
 * TOP BAR (.shell-head): a far-left **left-pane toggle**, the brand (bearing mark +
 * Fraunces wordmark), a **breadcrumb** (App-Space › Workspace › …surface crumbs),
 * and a far-right **AI-agent toggle** (opens the right pane). Nothing between the
 * breadcrumb and the AI toggle, so the two controls can never collide on mobile.
 *
 * FOOTER (.shell-foot): the **primary value pipeline** — the four app-spaces
 * (Workshops · Kompass · Suche · Bewerbungen), `aria-current` on the active one,
 * thumb-reachable on mobile. This is how you move ACROSS the pipeline / switch
 * context. The url-safe **resource-path** slug is pinned to its right (desktop only).
 *
 * LEFT PANE (.shell-nav): **context-dependent** navigation WITHIN the current
 * app-space — a space header + the sections of that space (Kompass → Profil ·
 * Lebenslauf · Belege · Nachweise, …), each able to carry an **indicator** badge
 * (count + severity, e.g. open gaps). Collapsible to an icon rail; the account
 * **avatar** sits at the foot (IdP picture or initials) and its popover is the home
 * for everything non-pipeline (Läufe, KI-Einstellungen, Theme, Onboarding, Abmelden).
 * The shell owns a sensible DEFAULT section set per space; the active surface may
 * REPLACE it (and attach live indicator counts) through the SectionNavBus.
 *
 * RIGHT PANE (.shell-aside): the **Platform AI Agent** panel — the plattform-wide
 * agent with access to all Platform Applications the user holds (Career Studio being
 * one; the list grows as access is granted). Toggled from the top bar's AI control.
 *
 * The breadcrumb is extensible: the shell always renders `App-Space › Workspace`;
 * the ACTIVE surface may push deeper crumbs (Sub-area, Step) through the
 * BreadcrumbBus. The <kit-outlet> mounts the active surface. No urn — a layout element.
 */

import {
  KitElement,
  effect,
  signal,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type RouteMatch,
  type Router,
  type ScopeChoice,
  type ScopeModel,
  type ScopeResolver,
  type ScopeSegment,
  type ScopeSelectDetail,
  type Signal,
  type ViewModel,
  SCOPE_SELECT_EVENT,
} from "@platform/web-kit"
import { classMap, html, ifDefined, nothing, svg, type TemplateResult } from "@platform/web-kit/render"
import {
  BreadcrumbToken,
  I18nToken,
  IngestStoreToken,
  RouterToken,
  SectionNavToken,
  SessionToken,
  apiGet,
  fetchWallet,
  CREDITS_UPDATED_EVENT,
  PAYMENT_REQUIRED_EVENT,
  SESSION_EXPIRED_EVENT,
  sessionExpiryHandler,
  type WalletView,
} from "../services"
import type { CareerI18n, CareerKey } from "../../i18n"
import {
  SETTINGS_GATE_ROLE,
  sessionCanConfigureLlm,
  type BreadcrumbBus,
  type Crumb,
  type CrumbChoice,
  type IngestRunView,
  type IngestStore,
  type SectionNavBus,
  type SectionNavItem,
  type SectionState,
  type SectionTone,
  type ShellSession,
} from "../domain"
import { GLYPH } from "../lib/icons"
import { fetchResolutionCounts } from "../lib/resolution"

/** id of the visually-hidden hint that gated user-menu links point at via aria-describedby. */
const GATED_HINT_ID = "career-gated-hint"
/** id of the top-right user-menu popover (native Popover API). */
const USER_POP_ID = "career-user-pop"
/** localStorage key the FOUC-guard script (index.html) and the toggle share. */
const THEME_KEY = "career-theme"
/** localStorage keys for the shell chrome layout (left sidebar collapse, right pane open). */
const NAV_KEY = "career-nav-collapsed"
const ASIDE_KEY = "career-aside-open"

type ThemeMode = "light" | "dark" | "auto"

/** Read a boolean layout flag from storage; default `fallback`. Storage-safe (SSR/test). */
function readFlag(key: string, fallback: boolean): boolean {
  try {
    const v = globalThis.localStorage?.getItem(key)
    if (v === "1") return true
    if (v === "0") return false
  } catch {
    // storage unavailable — use the default.
  }
  return fallback
}

/** Up to two uppercase initials from a display name (avatar fallback when no picture). */
function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return "?"
  const first = parts[0]![0] ?? ""
  const last = parts.length > 1 ? (parts.at(-1)![0] ?? "") : ""
  return (first + last).toUpperCase()
}

/** One surface-contributed crumb choice → one scope choice (url · step · group label). */
function toScopeChoice(choice: CrumbChoice): ScopeChoice {
  if (choice.heading) return { label: choice.label, heading: true }
  if (choice.url !== undefined) return { label: choice.label, current: choice.current, url: choice.url }
  if (choice.stepId !== undefined) return { label: choice.label, current: choice.current, id: choice.stepId }
  return { label: choice.label, current: choice.current }
}

/**
 * An area's identity ACROSS stages: its path below the stage prefix (`/compass/werte`
 * → `werte`). Two stages carry »the same area« when this part matches — that is what
 * the cascading resolution rule asks about when the stage changes.
 */
export function areaIdOf(match: string): string {
  const group = SECTION_GROUPS.find((candidate) => match === candidate.prefix || match.startsWith(`${candidate.prefix}/`))
  return group ? match.slice(group.prefix.length).replace(/^\//, "") : match
}

/**
 * Career's answer to the platform's cascading resolution rule (`ScopeResolver`,
 * @platform/web-kit): when a person switches one crumb, which of the FOLLOWING
 * crumbs still resolve?
 *
 * Only the AREA is re-addressable: the same area may exist under another stage
 * (`/compass/werte` → `/workshops/werte`), and then the person keeps their
 * place and lands there instead of at the bare stage. Everything a SURFACE
 * contributed (its sub-area, its step) is that surface's own state — once an
 * ancestor changes there is no surface left to address it in, so it drops, and
 * the ordered rule drops everything after it.
 */
export function careerScopeResolver(t: (key: CareerKey) => string): ScopeResolver {
  return (node, context) => {
    if (node.key !== "area") return null
    const stage = context.find((entry) => entry.key === "stage")?.id
    const group = SECTION_GROUPS.find((candidate) => candidate.prefix === stage)
    const item = group?.items.find((entry) => areaIdOf(entry.match) === node.id)
    if (!item) return null
    return { ...node, label: t(item.label), url: item.url }
  }
}

/** The BreadcrumbBus's crumb shape → the platform scope segment the nav element takes. */
function toScopeSegment(crumb: Crumb): ScopeSegment {
  return {
    label: crumb.label,
    ...(crumb.content ? { content: true } : {}),
    ...(crumb.choices && crumb.choices.length > 0 ? { choices: crumb.choices.map(toScopeChoice) } : {}),
  }
}

export interface NavItem {
  url: string
  /** Active-state path prefix. */
  match: string
  label: CareerKey
  /** Realm role required, else the entry is marked gated. */
  requiresRole?: string
  /** Custom gate predicate (role + org, etc.); when set, it decides the gated cue. */
  gate?: (session: ShellSession) => boolean
  /** Shown only once an ingest run exists (the review destination). */
  requiresRun?: boolean
  /** Shown only in devMode (client-only operator tools, e.g. the theme tuner). */
  devOnly?: boolean
}

// URL paths are ENGLISH code (AX-9); the `label` keys resolve to German link TEXT.
// The FOOTER pipeline bar shows EXACTLY these four PRIMARY app-spaces (§7,
// operator-ratified order): Workshops · Kompass · Suche · Bewerbungen — the primary
// way to move ACROSS the value pipeline / switch context.
export const PRIMARY_NAV: readonly NavItem[] = [
  { url: "/workshops", match: "/workshops", label: "nav.workshops" },
  { url: "/compass", match: "/compass", label: "nav.kompass" },
  { url: "/search", match: "/search", label: "nav.suche" },
  { url: "/applications", match: "/applications", label: "nav.bewerbungen" },
]

// Everything that is NOT a primary pipeline stage lives in the account popover at the
// foot of the left pane. Dev-only diagnostics (the push-plane heartbeat) are NOT user
// destinations.
// Onboarding is no longer a user-menu detour: providing material + resolving the AI's
// proposals is folded into the main flow AT the CV-Werkbank (`/compass/cv`), positioned on
// the golden path before Workshop 2 (operator direction 2026-07-28). The legacy `/onboarding*`
// routes redirect there.
export const USER_MENU_NAV: readonly NavItem[] = [
  { url: "/credits", match: "/credits", label: "nav.guthaben" },
  { url: "/runs", match: "/runs", label: "nav.laeufe" },
  { url: "/settings/ai", match: "/settings", label: "nav.einstellungen", requiresRole: SETTINGS_GATE_ROLE, gate: sessionCanConfigureLlm },
  // Prozess-Landkarten (Admin-Konsole). Same gate as the AI settings — both disclose model
  // choice and prompt text, which is the same class of thing — so the entry carries the same
  // honest `is-gated` cue instead of vanishing for the people it is not meant for.
  { url: "/console/processes", match: "/console", label: "nav.prozesse", requiresRole: SETTINGS_GATE_ROLE, gate: sessionCanConfigureLlm },
  { url: "/settings/theme", match: "/settings/theme", label: "nav.design", devOnly: true },
]

/** A default within-space section entry (label + icon are resolved at render). */
export interface SectionDef {
  url: string
  match: string
  label: CareerKey
  icon: SectionIcon
  indicator?: { count: number; tone: SectionTone }
  /** D3: a superseded destination — carries a »Veraltet« chip in the pane (still reachable). */
  stale?: boolean
  /** See {@link SectionNavItem.indexEntry} — an index entry never shows a done-check. */
  indexEntry?: boolean
}
type SectionIcon =
  | "overview" | "compass" | "profile" | "cv" | "evidence" | "assets" | "workshop" | "upload" | "review" | "runs" | "settings" | "credits"
  // Per-workshop marks (2026-07-30) — one glyph per workshop, echoing its hero image.
  | "wsCardgame" | "wsInterview" | "wsValues" | "wsSearch" | "wsDecision" | "wsWriting"

/** A left-pane section group: the sections of ONE app-space (or intake/account route). */
export interface SectionGroup {
  /** The path prefix that selects this group. */
  prefix: string
  /** The space title shown as the pane header (an existing nav label key). */
  titleKey: CareerKey
  items: readonly SectionDef[]
}

// The shell's DEFAULT section set per space. Kompass is the rich case (five sections);
// spaces without a deeper sub-nav yet show their primary section. A surface may REPLACE
// its group's items — and attach LIVE indicator counts — via the SectionNavBus.
export const SECTION_GROUPS: readonly SectionGroup[] = [
  {
    // D1/W0: the Workshops pane mirrors the dashboard's 3-stage W1 »Deine Werte« track
    // (Kartenspiel → Interview → Werte-Sektion) — state-aware at runtime from /api/workshops —
    // so the whole track is left-pane-reachable (closes the values/interview/sektion gaps). The
    // self_knowledge stepper is GONE (it redirects, D3). Suchprofil/Profiler are redesigned
    // surfaces (W4), no longer »Veraltet«: they carry their own `gate` state from the same view.
    prefix: "/workshops",
    titleKey: "nav.workshops",
    // 2026-07-30: every workshop entry is an INDEX ENTRY (`indexEntry`) carrying its OWN
    // glyph instead of the shared `workshop` star and instead of a ✓/◉/○ progress mark —
    // the pane is a stable list of workshops you can come back to and re-run, not a
    // checklist that freezes into "done". Gates are unchanged: `locked` still shows the
    // lock and the honest reason. The glyphs echo each workshop's hero image on the hub.
    items: [
      { url: "/workshops", match: "/workshops", label: "sectionnav.overview", icon: "overview" },
      { url: "/workshops/values", match: "/workshops/values", label: "workshop.w1.stage.cardgame", icon: "wsCardgame", indexEntry: true },
      { url: "/workshops/values/interview", match: "/workshops/values/interview", label: "workshop.w1.stage.interview", icon: "wsInterview", indexEntry: true },
      { url: "/workshops/values/sektion", match: "/workshops/values/sektion", label: "workshop.w1.stage.sektion", icon: "wsValues", indexEntry: true },
      { url: "/workshops/search-profile", match: "/workshops/search-profile", label: "sectionnav.ws.search", icon: "wsSearch", indexEntry: true },
      { url: "/workshops/profiler", match: "/workshops/profiler", label: "sectionnav.ws.profiler", icon: "wsDecision", indexEntry: true },
      // W4 »Schreibstil«. It unlocks off W1 (server-side `PREREQ.writing_style = self_knowledge`),
      // NOT off the W2→W3 chain, so its position here is presentation order only — the pane reads
      // each entry's own gate and must never imply it is the fourth link in a sequence.
      { url: "/workshops/writing", match: "/workshops/writing", label: "sectionnav.ws.writing", icon: "wsWriting", indexEntry: true },
    ],
  },
  {
    prefix: "/compass",
    titleKey: "nav.kompass",
    items: [
      // S567: `/compass` is the DOCUMENT, not an Übersicht — so it is named for what it is.
      // The former »Profil« entry is GONE: it pointed at the same document under a second
      // name, which is exactly the ambiguity the ratified plan retires. While the document
      // is mounted it splices its own section anchors in beneath this entry (see
      // `sectionItems`), so the pane keeps its route vocabulary AND gains the outline.
      { url: "/compass", match: "/compass", label: "sectionnav.kompass.doc", icon: "compass" },
      // »Meine Werte« — the durable self-view (closes the /compass/werte orientation gap, W0).
      { url: "/compass/werte", match: "/compass/werte", label: "surface.werte.title", icon: "workshop" },
      { url: "/compass/cv", match: "/compass/cv", label: "surface.lebenslauf.title", icon: "cv" },
      // No hardcoded indicator: a fabricated count would contradict the real "N offen" the
      // Belege surface shows (evidence-grade honesty). The affordance + badge rendering are
      // REAL — the evidence surface publishes a LIVE open-gap count (tone by severity) through
      // the SectionNavBus (seam in app/domain.ts); the badge appears once that count is wired.
      { url: "/compass/evidence", match: "/compass/evidence", label: "surface.belege.title", icon: "evidence" },
      { url: "/compass/assets", match: "/compass/assets", label: "surface.nachweise.title", icon: "assets" },
    ],
  },
  {
    prefix: "/search",
    titleKey: "nav.suche",
    items: [{ url: "/search", match: "/search", label: "sectionnav.overview", icon: "overview" }],
  },
  {
    prefix: "/applications",
    titleKey: "nav.bewerbungen",
    items: [{ url: "/applications", match: "/applications", label: "sectionnav.overview", icon: "overview" }],
  },
  {
    prefix: "/credits",
    titleKey: "nav.guthaben",
    items: [{ url: "/credits", match: "/credits", label: "sectionnav.overview", icon: "credits" }],
  },
  {
    prefix: "/runs",
    titleKey: "nav.laeufe",
    items: [{ url: "/runs", match: "/runs", label: "sectionnav.overview", icon: "runs" }],
  },
  {
    prefix: "/settings",
    titleKey: "nav.einstellungen",
    items: [{ url: "/settings/ai", match: "/settings/ai", label: "sectionnav.overview", icon: "settings" }],
  },
  {
    // Admin-Konsole. Like »/settings«, it has no root entry of its own — the space IS its one
    // section, so the pane keeps the route vocabulary without inventing an Übersicht above it.
    prefix: "/console",
    titleKey: "nav.prozesse",
    items: [{ url: "/console/processes", match: "/console/processes", label: "sectionnav.overview", icon: "runs" }],
  },
]

const THEME_MODES: readonly ThemeMode[] = ["light", "dark", "auto"]
const THEME_LABEL: Record<ThemeMode, CareerKey> = {
  light: "theme.light",
  dark: "theme.dark",
  auto: "theme.auto",
}

/** True on a phone-width viewport (the left pane is an overlay drawer, not an inline rail).
 *  Storage/SSR/test-safe — defaults to false (desktop) when matchMedia is unavailable. */
function isMobileViewport(): boolean {
  try {
    return globalThis.matchMedia?.("(max-width: 720px)")?.matches ?? false
  } catch {
    return false
  }
}

/** Read the persisted theme mode; default "auto" (follow OS). Storage-safe. */
function readThemeMode(): ThemeMode {
  try {
    const m = globalThis.localStorage?.getItem(THEME_KEY)
    if (m === "light" || m === "dark") return m
  } catch {
    // storage unavailable (SSR/test) — fall through to auto.
  }
  return "auto"
}

/* The icon set now lives in `app/lib/icons.ts` (R2) — ONE stroke vocabulary the whole app
 * shares, instead of a const private to this file plus hand-rolled copies in the surfaces.
 * The shell keeps using the raw `GLYPH` fragments because it owns its own `<svg>` wrappers
 * and sizes them from the chrome CSS; new code uses `icon(name)` from the same module. */

/** The plan-rail state → its leading mark (done ✓ / current ◉ / open ○ / attention ●;
 *  locked renders the lock glyph). */
const STATE_MARK: Record<Exclude<SectionState, "locked">, string> = { done: "✓", current: "◉", open: "○", attention: "●" }

/** Sections whose badge counts something SPECIFIC enough to deserve its own words — so two
 *  badges in the same pane never read as one quantity disagreeing with itself. Anything not
 *  listed keeps the generic »{n} offen«. */
const INDICATOR_LABEL_KEY: Record<string, CareerKey> = {
  "/compass/cv": "sectionnav.indicator.cvDecisions",
  "/compass/evidence": "sectionnav.indicator.evidenceGaps",
}

/** An indicator that asks for the person's attention (as opposed to merely informing). */
function needsAttention(indicator: { tone: SectionTone } | undefined): boolean {
  return indicator?.tone === "warn" || indicator?.tone === "critical"
}

/** Mark a surface-contributed entry as an in-document ANCHOR — subordinate to the route entries
 *  it sits under, and never carrying a plan-rail state (an outline heading is not a step). */
function asAnchor(item: SectionNavItem): SectionNavItem {
  const { state: _state, ...rest } = item
  return { ...rest, kind: "anchor" }
}

/**
 * The match of the deepest entry a path resolves to (longest match wins) — PURE over a given
 * list. It is a free function rather than a method on purpose: `sectionItems()` needs the active
 * entry to know where to splice a document's anchors, and resolving that through the
 * `isSectionActive` accessor made `sectionItems → isSectionActive → activeSectionMatch →
 * sectionItems` a cycle that blew the stack the moment any surface contributed an outline.
 * Taking the list as an argument makes the dependency one-way and the recursion impossible.
 */
function deepestMatch(items: readonly SectionNavItem[], p: string): string | undefined {
  return items.filter((it) => p === it.match || p.startsWith(`${it.match}/`)).sort((a, b) => b.match.length - a.match.length)[0]?.match
}

/** A compact stroke icon per pipeline space (18px, currentColor) for the footer bar. */
function navIcon(label: CareerKey): TemplateResult {
  const icons: Partial<Record<CareerKey, TemplateResult>> = {
    "nav.onboarding": GLYPH.upload,
    "nav.review": GLYPH.review,
    "nav.workshops": GLYPH.workshops,
    "nav.kompass": GLYPH.compass,
    "nav.suche": GLYPH.search,
    "nav.bewerbungen": GLYPH.applications,
    "nav.laeufe": GLYPH.runs,
    "nav.prozesse": GLYPH.flow,
    "nav.einstellungen": GLYPH.settings,
    "nav.guthaben": GLYPH.wallet,
    "nav.profil": GLYPH.user,
  }
  return html`<svg viewBox="0 0 24 24" aria-hidden="true">${icons[label] ?? svg`<circle cx="12" cy="12" r="4" />`}</svg>`
}

/** A compact stroke icon per left-pane SECTION (16px). Unknown key → a neutral dot. */
function sectionIconGlyph(icon: string | undefined): TemplateResult {
  const icons: Record<SectionIcon, TemplateResult> = {
    overview: GLYPH.overview,
    compass: GLYPH.compass,
    profile: GLYPH.user,
    cv: GLYPH.cv,
    evidence: GLYPH.evidence,
    assets: GLYPH.assets,
    workshop: GLYPH.workshops,
    upload: GLYPH.upload,
    review: GLYPH.review,
    runs: GLYPH.runs,
    settings: GLYPH.settings,
    credits: GLYPH.wallet,
    wsCardgame: GLYPH.wsCardgame,
    wsInterview: GLYPH.wsInterview,
    wsValues: GLYPH.wsValues,
    wsSearch: GLYPH.wsSearch,
    wsDecision: GLYPH.wsDecision,
    wsWriting: GLYPH.wsWriting,
  }
  const glyph = icon && icon in icons ? icons[icon as SectionIcon] : svg`<circle cx="12" cy="12" r="3.5" />`
  return html`<svg viewBox="0 0 24 24" aria-hidden="true">${glyph}</svg>`
}

/** One footer pipeline-space link (`foot-item`) — icon + label, `aria-current` on active. */
function footAnchor(vm: ShellVm, item: NavItem): TemplateResult {
  const isActive = vm.isActive(item)
  return html`<a
    href=${item.url}
    data-act="nav"
    data-url=${item.url}
    class=${classMap({ "foot-item": true, "is-current": isActive })}
    aria-current=${ifDefined(isActive ? "page" : undefined)}
    title=${vm.t(item.label)}
    lang="de"
    >${navIcon(item.label)}<span class="foot-item-label">${vm.t(item.label)}</span></a
  >`
}

/**
 * The leading mark for a section entry. Three vocabularies, in priority order:
 *   - an in-document ANCHOR is not a place and not a step → a hairline tick;
 *   - `locked` → the lock, in EVERY space (AX-10: gated ≠ broken);
 *   - an INDEX ENTRY (the Workshops pane, 2026-07-30) or a stateless destination → its own
 *     semantic icon. An index entry deliberately never shows ✓: see {@link SectionNavItem.indexEntry};
 *   - anything else → the S4 plan-rail mark (done ✓ / current ◉ / open ○ / attention ●).
 */
function sectionMark(item: SectionNavItem): TemplateResult {
  const { state } = item
  if (item.kind === "anchor") return html`<span class="nav-anchor-mark" aria-hidden="true"></span>`
  if (state === "locked") return html`<span class="nav-state-mark" aria-hidden="true"><svg viewBox="0 0 24 24">${GLYPH.lock}</svg></span>`
  if (!state || item.indexEntry === true) return sectionIconGlyph(item.icon)
  return html`<span class="nav-state-mark" aria-hidden="true">${STATE_MARK[state]}</span>`
}

/**
 * One left-pane section link (`nav-item`) rendered as a PLAN-RAIL row (D1): a state mark +
 * label (with a »Veraltet« chip on superseded entries) + a trailing note — the locked reason
 * (German, AX-10), else a live indicator badge, else an honest state detail. `aria-current`
 * marks the section the route is on. Labels/notes fold away when the pane collapses to a rail.
 */
function sectionAnchor(vm: ShellVm, item: SectionNavItem): TemplateResult {
  const isActive = vm.isSectionActive(item)
  const ind = item.indicator
  const state = item.state
  const locked = state === "locked"
  const stateLabel = state ? vm.t(`sectionnav.state.${state}` as CareerKey) : undefined
  return html`<a
    href=${item.url}
    data-act="nav"
    data-url=${item.url}
    class=${classMap({ "nav-item": true, "is-anchor": item.kind === "anchor", "is-current": isActive, ...(state ? { [`is-${state}`]: true } : {}) })}
    aria-current=${ifDefined(isActive ? "page" : undefined)}
    aria-label=${ifDefined(stateLabel ? `${item.label} — ${stateLabel}` : undefined)}
    title=${locked && item.lockedReason ? item.lockedReason : item.label}
    lang="de"
    >${sectionMark(item)}<span class="nav-item-label" lang="de"
      >${item.label}${item.stale ? html`<span class="nav-stale-chip is-stale" title=${vm.t("sectionnav.stale.hint")} lang="de">${vm.t("sectionnav.stale.badge")}</span>` : nothing}</span
    >${
      locked && item.lockedReason
        ? html`<span class="nav-locked-reason" lang="de">${item.lockedReason}</span>`
        : ind
          ? html`<span
              class=${classMap({ "nav-badge": true, [`is-${ind.tone}`]: true })}
              title=${ifDefined(ind.label)}
              aria-label=${ifDefined(ind.label)}
              >${ind.count}</span
            >`
          : item.detail
            ? html`<span class="nav-detail" lang="de">${item.detail}</span>`
            : nothing
    }</a
  >`
}

/** One user-menu link (`user-item`) — carries active + gated (is-gated + hint) cues. */
function userMenuAnchor(vm: ShellVm, item: NavItem): TemplateResult {
  const isActive = vm.isActive(item)
  const gated = vm.isGated(item)
  return html`<a
    href=${item.url}
    data-act="nav"
    data-url=${item.url}
    data-gated=${ifDefined(gated ? "true" : undefined)}
    class=${classMap({ "user-item": true, "is-current": isActive, "is-gated": gated })}
    aria-current=${ifDefined(isActive ? "page" : undefined)}
    aria-describedby=${ifDefined(gated ? GATED_HINT_ID : undefined)}
    title=${ifDefined(gated ? vm.t("gated.hint") : undefined)}
    lang="de"
    >${navIcon(item.label)}<span class="user-item-label">${vm.t(item.label)}</span></a
  >`
}

/** The plan-rail state a section carries, derived from live BFF (never fabricated). */
interface RailState {
  state: SectionState
  detail?: string
  lockedReason?: string
}

type StageStatus = "done" | "current" | "todo"
const stageStatus = (v: unknown): StageStatus => (v === "done" || v === "current" ? v : "todo")
const asNum = (v: unknown): number | null => (typeof v === "number" ? v : null)

/** The `/api/workshops` list gate, mapped onto the pane's plan-rail vocabulary (W2/W3). */
/**
 * Whether a W1 stage reads `locked`: its predecessor is unfinished AND the stage has no
 * evidence of its own. A stage that is DONE is never locked — the Werte-Sektion can be
 * synthesized from the pyramid alone, so gating it on the interview told a person who had
 * already produced one that they could not go there. That is the same "the track must be
 * sequential" assumption the server-side `w1TrackState` back-fill was removed for; a lock is
 * only honest about work NOT yet done (AX-10: gated ≠ broken).
 */
function isGated(self: StageStatus, prerequisite: StageStatus): boolean {
  return self !== "done" && prerequisite !== "done"
}

const RAIL_STATE_BY_GATE: Record<string, SectionState> = {
  completed: "done",
  in_progress: "current",
  locked: "locked",
  available: "open",
}

/**
 * D1: map the WHOLE `/api/workshops` view onto the Workshops-pane section URLs (NO new
 * endpoint — this is the same list the dashboard reads).
 *
 * W1 »Deine Werte« contributes its 3-stage track (cardgame → interview → sektion): each stage
 * → done ✓ / current ◉ / open ○; a stage whose predecessor is unfinished is `locked` with an
 * honest German reason (AX-10: gated ≠ broken).
 *
 * W2 »Suchprofil« and W3 »Profiler« contribute their `gate` — the SAME field the workshop hub
 * already renders, which previously never reached the pane, so a finished Suchprofil still read
 * ○ offen next to a ✓ Kartenspiel. `/workshops` (Übersicht) deliberately gets NO state: it is a
 * destination you visit, not a step you complete.
 *
 * Absence of a workshop leaves its entry stateless (never a fabricated state).
 */
export function deriveWorkshopSectionState(workshops: ReadonlyArray<Record<string, unknown>>, t: CareerI18n["t"]): Record<string, RailState> {
  const byType = new Map(workshops.map((w) => [String(w["type"]), w]))
  const w1 = byType.get("self_knowledge")
  const stages = Array.isArray(w1?.["stages"]) ? (w1["stages"] as Array<Record<string, unknown>>) : []
  const byKey = new Map(stages.map((s) => [String(s["key"]), s]))
  const statusOf = (k: string): StageStatus => stageStatus(byKey.get(k)?.["status"])
  const openOrCurrent = (s: StageStatus): SectionState => (s === "done" ? "done" : s === "current" ? "current" : "open")
  const out: Record<string, RailState> = {}

  const cg = statusOf("cardgame")
  out["/workshops/values"] = { state: openOrCurrent(cg), ...(cg === "done" ? { detail: t("workshop.w1.detail.pyramidDone") } : {}) }

  const iv = statusOf("interview")
  const ivStage = byKey.get("interview")
  const round = asNum(ivStage?.["round"])
  const roundTotal = asNum(ivStage?.["roundTotal"])
  out["/workshops/values/interview"] =
    isGated(iv, cg)
      ? { state: "locked", lockedReason: t("sectionnav.locked.afterCardgame") }
      : {
          state: openOrCurrent(iv),
          ...(iv === "done"
            ? { detail: t("workshop.w1.detail.interviewDone") }
            : round !== null && roundTotal !== null
              ? { detail: t("sectionnav.detail.round", { round, total: roundTotal }) }
              : {}),
        }

  const sk = statusOf("sektion")
  out["/workshops/values/sektion"] =
    isGated(sk, iv)
      ? { state: "locked", lockedReason: t("sectionnav.locked.afterInterview") }
      : { state: openOrCurrent(sk), ...(sk === "done" ? { detail: t("workshop.w1.detail.sektionDone") } : {}) }

  // W2/W3: the list's own `gate` — the completion the pane used to drop on the floor.
  const fromGate = (type: string, url: string, lockedReason: CareerKey): void => {
    const gate = byType.get(type)?.["gate"]
    const state = typeof gate === "string" ? RAIL_STATE_BY_GATE[gate] : undefined
    if (!state) return // no such workshop / unknown gate → a plain entry, never a guess.
    out[url] = state === "locked" ? { state, lockedReason: t(lockedReason) } : { state }
  }
  fromGate("search_profile", "/workshops/search-profile", "sectionnav.locked.afterSektion")
  fromGate("profiler", "/workshops/profiler", "sectionnav.locked.afterSearchProfile")

  return out
}

export class ShellVm implements ViewModel {
  readonly current: ReadonlySignal<RouteMatch | null>
  /** Cold-load probe: a run may exist before any surface has subscribed the Ingest stream. */
  private readonly fetchedHasRun: Signal<boolean> = signal(false)
  /** Live: the push-fed ingest store gains a run the moment onboarding/review subscribes. */
  private readonly runs: ReadonlySignal<readonly IngestRunView[]>
  /** Active theme mode — drives the toggle's aria-pressed and the <html> data-theme. */
  private readonly theme: Signal<ThemeMode> = signal(readThemeMode())
  /** DESKTOP: the left pane collapsed to an icon rail (persisted). */
  private readonly navCollapsed: Signal<boolean> = signal(readFlag(NAV_KEY, false))
  /** MOBILE: the left pane overlay drawer open (ephemeral; default CLOSED — never open on load). */
  private readonly navDrawerOpen: Signal<boolean> = signal(false)
  /** Right AI-agent pane open (persisted; default closed). */
  private readonly asideOpen: Signal<boolean> = signal(readFlag(ASIDE_KEY, false))
  /** The credit wallet for the balance chip (fetched on attach; refreshed on a purchase). */
  private readonly wallet: Signal<WalletView | null> = signal<WalletView | null>(null)
  /** The shared credits notice is showing (402 insufficient OR entitlement-403 blocked). */
  private readonly paymentNotice: Signal<boolean> = signal(false)
  /** I7: the politely-announced destination title on a user-initiated route change (empty on load). */
  private readonly announcement: Signal<string> = signal("")
  /** I7: the path last settled — null until the FIRST route resolves (initial load: no focus/announce). */
  private lastAnnouncedPath: string | null = null
  /** I7: set on a user navigation; the element consumes it AFTER the outlet re-renders → focus <main>. */
  private focusMainPending = false
  /** Which copy the notice shows: `blocked` (zero-balance first run) vs `insufficient` (has some, not enough). */
  private readonly paymentNoticeKind: Signal<"insufficient" | "blocked"> = signal("insufficient")
  /** The session-expired re-auth prompt is showing (raised globally by the shared fetch layer on a 401). */
  private readonly sessionExpired: Signal<boolean> = signal(false)
  /** D1: live plan-rail state per section URL (from the workshops track); empty ⇒ plain default. */
  private readonly areaSectionState: Signal<Readonly<Record<string, RailState>>> = signal<Readonly<Record<string, RailState>>>({})
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]
  /** Bound window listeners (removed on detach). */
  private readonly onPaymentRequired = (e: Event): void => {
    const kind = (e as CustomEvent<{ kind?: "insufficient" | "blocked" }>).detail?.kind
    this.paymentNoticeKind.set(kind === "blocked" ? "blocked" : "insufficient")
    this.paymentNotice.set(true)
    void this.loadWallet()
  }
  private readonly onCreditsUpdated = (): void => void this.loadWallet()
  /** A data-plane 401 landed → ask the user to re-authenticate (their work is already safe). */
  private readonly onSessionExpired = (): void => this.sessionExpired.set(true)

  constructor(
    private readonly router: Router,
    i18n: CareerI18n,
    readonly session: ShellSession,
    ingestStore: IngestStore,
    /** The surface-contributed deeper crumbs (Sub-area, Step) — reactive. */
    private readonly breadcrumb: BreadcrumbBus,
    /** The surface-contributed left-pane section nav — reactive. */
    private readonly sectionNav: SectionNavBus,
  ) {
    this.current = router.current
    this.t = i18n.t
    this.runs = ingestStore.list("aktiv")
    this.renderDeps = [
      this.current,
      this.fetchedHasRun,
      this.runs,
      this.theme,
      this.navCollapsed,
      this.navDrawerOpen,
      this.asideOpen,
      this.breadcrumb.crumbs,
      this.sectionNav.items,
      this.wallet,
      this.paymentNotice,
      this.sectionNav.indicators,
      this.announcement,
      this.paymentNoticeKind,
      this.areaSectionState,
      this.sessionExpired,
    ]
  }

  /** Learn whether an ingest run exists, so the review menu entry appears (M2). Both the
   *  cold-load fetch AND the live store feed it, so it appears in-session too. Also loads the
   *  balance chip and subscribes the shared 402 / purchase-landed signals (Step 5). */
  attach(): () => void {
    if (this.session.user) {
      void this.loadRuns()
      void this.loadWallet()
      void this.loadWorkshopState()
      void this.loadResolutionState()
    }
    globalThis.addEventListener?.(PAYMENT_REQUIRED_EVENT, this.onPaymentRequired)
    globalThis.addEventListener?.(CREDITS_UPDATED_EVENT, this.onCreditsUpdated)
    globalThis.addEventListener?.(SESSION_EXPIRED_EVENT, this.onSessionExpired)
    // I7 (WCAG 2.4.3 / 4.1.3): on a USER-INITIATED route change — not the initial load,
    // not a same-URL re-render — announce the destination surface politely and flag focus
    // to move to <main> after the outlet re-renders. The initial load is left untouched so
    // the skip-link stays first-focusable; a popstate while a field is focused is STILL a
    // navigation, so it still moves focus (we only skip the very first settle).
    const stopRouteFx = effect([this.current], () => {
      const match = this.current.value
      if (!match) return // route not resolved yet — the shell can mount before router.start settles.
      const { path } = match
      if (this.lastAnnouncedPath === null) {
        this.lastAnnouncedPath = path // FIRST resolved route (initial load) — baseline, DON'T steal focus.
        return
      }
      if (path === this.lastAnnouncedPath) return // same-URL re-render — nothing to announce.
      const prev = this.lastAnnouncedPath
      this.lastAnnouncedPath = path
      this.announcement.set(this.routeTitle())
      this.focusMainPending = true
      // Keep the »needs-you« CV badge honest as the person resolves: refresh when they arrive at
      // or leave the Kompass (where the Werkbank lives) — bounded, not on every hop.
      if (this.session.user && (path.startsWith("/compass") || prev.startsWith("/compass"))) void this.loadResolutionState()
    })
    return () => {
      globalThis.removeEventListener?.(PAYMENT_REQUIRED_EVENT, this.onPaymentRequired)
      globalThis.removeEventListener?.(CREDITS_UPDATED_EVENT, this.onCreditsUpdated)
      globalThis.removeEventListener?.(SESSION_EXPIRED_EVENT, this.onSessionExpired)
      stopRouteFx()
    }
  }

  /** I7: the polite live-region text — the last announced destination-surface title. */
  announcementText(): string {
    return this.announcement.value
  }

  /**
   * I7: the human title for the current route. Within a space, the active SECTION label
   * (e.g. "Belege", "Profil") — UNLESS it's the space-root / overview section (its match
   * equals the space prefix), where the app-space label ("Kompass", "Suche") reads better;
   * else the active app-space / user-menu label, else the app name. Reuses the SAME nav
   * machinery the section pane and breadcrumb already resolve — no parallel route→title map.
   */
  routeTitle(): string {
    const active = this.activeItem()
    const spaceLabel = active ? this.t(active.label) : this.t("app.name")
    const group = this.sectionGroup()
    const match = this.activeSectionMatch()
    if (group && match && match !== group.prefix) {
      const item = this.sectionItems().find((it) => it.match === match)
      if (item) return item.label
    }
    return spaceLabel
  }

  /** I7: consume the one pending post-navigation focus move (element calls this after render). */
  takePendingMainFocus(): boolean {
    if (!this.focusMainPending) return false
    this.focusMainPending = false
    return true
  }

  private async loadRuns(): Promise<void> {
    try {
      const runs = await apiGet<unknown[]>("/api/ingest")
      this.fetchedHasRun.set(runs.length > 0)
    } catch {
      // signed out or unreachable — leave the review entry hidden.
    }
  }

  /** D1: derive the Workshops-pane plan-rail state from the SAME `/api/workshops` the dashboard
   *  reads — the W1 3-stage track AND the W2/W3 gates. Failure leaves the pane on its plain
   *  (stateless) default. */
  private async loadWorkshopState(): Promise<void> {
    try {
      const list = await apiGet<Array<Record<string, unknown>>>("/api/workshops")
      this.areaSectionState.set(deriveWorkshopSectionState(Array.isArray(list) ? list : [], this.t))
    } catch {
      // signed out / unreachable / no track yet — the pane's entries stay stateless (plain
      // destinations with their semantic icon), never a fabricated ○ »offen«.
    }
  }

  /**
   * The »needs-you« resolution badge on the CV-Werkbank (`/compass/cv`). The number comes from
   * the SHARED derivation (`app/lib/resolution.ts`) the Werkbank's hero chip also consumes — one
   * arithmetic, so the badge, the chip and the body copy can never show different totals again
   * (they used to disagree by the promotable-documents term: 24 vs 25). Composed from EXISTING
   * reads only (no new endpoint, no events). Publishes a warn-tone count to the section-nav bus
   * so the badge shows app-wide on a cold load (distinct from the live Belege gap badge on
   * `/compass/evidence`, which counts the narrower open-gap subset). Refreshed on attach and
   * whenever the person arrives at or leaves the Kompass, so resolving clears it.
   */
  private async loadResolutionState(): Promise<void> {
    try {
      const { total } = await fetchResolutionCounts()
      this.sectionNav.setIndicator("/compass/cv", total > 0 ? { count: total, tone: "warn" } : null)
    } catch {
      // signed out / unreachable / nothing to resolve — no badge (never a fabricated count).
    }
  }

  /** Fetch the wallet for the balance chip; failure hides the chip (never a hard error). */
  private async loadWallet(): Promise<void> {
    try {
      this.wallet.set(await fetchWallet())
    } catch {
      // signed out, unreachable, or billing not yet live — the chip simply doesn't render.
    }
  }

  /** The current balance for the chip, or null when unknown (hides the chip). */
  walletBalance(): number | null {
    return this.wallet.value?.balance ?? null
  }

  /** Whether the shared credits notice is visible. */
  showPaymentNotice(): boolean {
    return this.paymentNotice.value
  }

  /** The notice copy key — `blocked` (nothing yet, get started) vs `insufficient` (not enough for this). */
  paymentNoticeKey(): CareerKey {
    return this.paymentNoticeKind.value === "blocked" ? "credits.notice.blocked" : "credits.notice.insufficient"
  }

  /** Dismiss the 402 notice (user acknowledged / navigated to top up). */
  dismissPaymentNotice(): void {
    this.paymentNotice.set(false)
  }

  /** Whether the session-expired re-auth prompt is showing. */
  showSessionExpired(): boolean {
    return this.sessionExpired.value
  }

  /** Re-authenticate: full-page OIDC round-trip back to the current route (local drafts survive it). */
  relogin(): void {
    sessionExpiryHandler.redirect()
  }

  private get hasRun(): boolean {
    return this.fetchedHasRun.value || this.runs.value.length > 0
  }

  get path(): string {
    return this.current.value?.path ?? "/"
  }

  /**
   * The url-safe SLUG path to the resource/asset in view — the footer resource-path (§7).
   * DISTINCT from the human breadcrumb: it addresses the resource itself in slug form (e.g.
   * `search/opportunities/abc123`), never a display-name. Derived from the route pathname, whose
   * segments are already English code slugs (AX-9). Empty at the app root (nothing to address).
   *
   * FUTURE (§7, deferred): this seam will lazily disclose the resource's RBAC/ReBAC access-control
   * graph. Today it renders the slug path only — keep this the single source of the path segments.
   */
  resourcePathSegments(): string[] {
    return this.path.split("/").filter((s) => s.length > 0)
  }

  /** The four primary app-spaces — always in the footer pipeline bar. */
  primaryItems(): NavItem[] {
    return [...PRIMARY_NAV]
  }

  /** The user-menu destinations (review appears once a run exists). */
  userMenuItems(): NavItem[] {
    return USER_MENU_NAV.filter((item) => (!item.requiresRun || this.hasRun) && (!item.devOnly || this.session.devMode))
  }

  isActive(item: NavItem): boolean {
    return this.path === item.match || this.path.startsWith(`${item.match}/`)
  }

  isGated(item: NavItem): boolean {
    if (item.gate) return !item.gate(this.session)
    return false
  }

  /** The destination the user is currently in — primary space OR user-menu route. */
  private activeItem(): NavItem | undefined {
    return [...this.primaryItems(), ...this.userMenuItems()].find((item) => this.isActive(item))
  }

  /** The section GROUP whose route family the current path belongs to (longest prefix wins). */
  private sectionGroup(): SectionGroup | undefined {
    const p = this.path
    return [...SECTION_GROUPS]
      .sort((a, b) => b.prefix.length - a.prefix.length)
      .find((g) => p === g.prefix || p.startsWith(`${g.prefix}/`))
  }

  /** The left-pane header title for the current space (its space label, or the app name). */
  sectionTitle(): string {
    const group = this.sectionGroup()
    return group ? this.t(group.titleKey) : this.t("app.name")
  }

  /** The left-pane header icon key (mirrors the space glyph) for the current space. */
  sectionTitleIcon(): SectionIcon {
    const key = this.sectionGroup()?.titleKey
    switch (key) {
      case "nav.workshops":
        return "workshop"
      case "nav.kompass":
        return "overview"
      case "nav.suche":
        return "overview"
      case "nav.bewerbungen":
        return "cv"
      case "nav.onboarding":
        return "upload"
      case "nav.laeufe":
        return "runs"
      case "nav.guthaben":
        return "credits"
      case "nav.einstellungen":
        return "settings"
      default:
        return "overview"
    }
  }

  /** True when there IS a section context to render a left pane for. */
  hasSections(): boolean {
    return this.sectionItems().length > 0
  }

  /**
   * The left-pane sections to render: a surface's CONTRIBUTED items when present (they carry
   * live labels + indicators), else the shell's per-space DEFAULT set resolved through `t()`.
   */
  sectionItems(): SectionNavItem[] {
    const contributed = this.sectionNav.items.value
    const group = this.sectionGroup()
    // A contributing surface used to REPLACE the pane wholesale, so the space's rail vanished
    // and the contributed entries — no icon, no state — read as a third, silently different
    // pane vocabulary (WS-A handoff flag). They are now spliced in as explicitly SUBORDINATE
    // in-document anchors beneath the entry the route is on: one vocabulary, and the pane stays
    // the space's stable orientation device instead of disappearing under the document.
    if (!group) return contributed.map(asAnchor)
    // A surface owns nothing here → the shell's per-space DEFAULT set, resolved to a state-aware
    // plan rail (D1): live indicators (Belege → open gaps, #32) merged by URL, and the plan-rail
    // STATE overlaid — but ONLY where a real state exists (the Workshops track's
    // done/current/locked/open from /api/workshops, or `attention` from a warn/critical badge).
    // Everything else stays a plain destination, no mark.
    const live = this.sectionNav.indicators.value
    const rail = this.areaSectionState.value
    const base = group.items.map((it) => {
      const indicator = live[it.url] ?? it.indicator
      const derived = rail[it.url]
      // A stateless entry stays STATELESS: it renders its semantic icon, not a ○ »offen« mark.
      // Übersicht is a destination you visit, not a step you complete — and neither are the
      // Kompass/Suche/Bewerbungen/Guthaben/Läufe/Einstellungen entries. `is-current` (you-are-
      // here) is applied independently by `sectionAnchor` from `isSectionActive`, so nothing is
      // lost by leaving `state` undefined. A section with no progress state but a warn/critical
      // indicator reads `attention` — the one honest "this needs you" mark.
      const state = derived?.state ?? (needsAttention(indicator) ? "attention" : undefined)
      return {
        url: it.url,
        match: it.match,
        label: this.t(it.label),
        icon: it.icon,
        ...(state ? { state } : {}),
        ...(derived?.detail ? { detail: derived.detail } : {}),
        ...(derived?.lockedReason ? { lockedReason: derived.lockedReason } : {}),
        ...(it.stale ? { stale: true } : {}),
        ...(it.indexEntry ? { indexEntry: true } : {}),
        ...(indicator ? { indicator: { ...indicator, label: this.indicatorLabel(it.url, indicator.count) } } : {}),
      }
    })
    if (contributed.length === 0) return base
    // Resolve the active entry against `base` DIRECTLY — never through `isSectionActive`, which
    // reads `sectionItems()` and would re-enter this method (see {@link deepestMatch}).
    const activeMatch = deepestMatch(base, this.path)
    const at = base.findIndex((it) => it.match === activeMatch)
    const anchors = contributed.map(asAnchor)
    return at < 0 ? [...base, ...anchors] : [...base.slice(0, at + 1), ...anchors, ...base.slice(at + 1)]
  }

  /**
   * What a section's badge SAYS it counts. Each badge names its own quantity, so the CV
   * badge (every open decision on the Lebenslauf) can never be read as the Belege badge
   * (the narrower subset of statements still lacking a Beleg) — they are different numbers
   * about different work, and a shared »{n} offen« made them look like one number diverging.
   */
  private indicatorLabel(url: string, n: number): string {
    const key = INDICATOR_LABEL_KEY[url]
    return this.t(key ?? "sectionnav.indicator.open", { n })
  }

  /** The match of the deepest section the current path resolves to (longest match wins).
   *  An in-page `#anchor` can never satisfy the match test, so a spliced outline never wins. */
  private activeSectionMatch(): string | undefined {
    return deepestMatch(this.sectionItems(), this.path)
  }

  isSectionActive(item: SectionNavItem): boolean {
    return item.match === this.activeSectionMatch()
  }

  /**
   * The platform SCOPE MODEL for `<kit-scope-nav>` — derived from ROUTING, never
   * hardcoded. The four levels map onto career like this:
   *
   *   space   → `personal` (Career is a personal-space application; it colors the mark)
   *   app     → »Career«. Static: career knows no URL of a sibling application, and a
   *             one-entry dropdown would be a lie. The cross-app switcher is a platform
   *             concern (a registry of installed apps), not this shell's.
   *   section → the active PRIMARY_NAV stage (Workshops · Kompass · Suche · Bewerbungen),
   *             its dropdown being the other three — the move ACROSS the pipeline.
   *   path    → the active LEFT-PANE section (its dropdown = that space's sibling
   *             sections), then whatever the active surface contributed to the
   *             BreadcrumbBus (Sub-area, Step — each with its own siblings).
   *
   * The left-pane section is folded in only when it is DEEPER than the stage itself:
   * on `/workshops` the section is »Übersicht«, and »Workshops › Übersicht« says nothing.
   */
  scopeModel(): ScopeModel {
    const active = this.activeItem()
    const section: ScopeSegment = {
      // `key`/`id` make the crumb resolvable: the cascading rule (@platform/web-kit)
      // asks the app what still exists once this level changes — see `scopeResolver`.
      key: "stage",
      ...(active ? { id: active.match } : {}),
      label: active ? this.t(active.label) : this.t("app.name"),
      menuLabel: this.t("breadcrumb.switchStage"),
      choices: PRIMARY_NAV.map((item) => ({ label: this.t(item.label), current: this.isActive(item), url: item.url })),
    }
    return {
      space: "personal",
      homeUrl: "/search",
      app: { label: this.t("app.name") },
      section,
      path: [...this.areaSegment(), ...this.breadcrumb.crumbs.value.map((crumb) => toScopeSegment(crumb))],
    }
  }

  /**
   * The app's answer to »does this crumb still resolve under the new context?«
   * (`ScopeResolver`, @platform/web-kit). Only the AREA is re-addressable: the same
   * area may exist under another stage (`/compass/werte` → `/workshops/werte`),
   * in which case the person keeps their place and lands there. Everything a SURFACE
   * contributed (its sub-area, its step) belongs to that surface — once an ancestor
   * changes there is no surface left to address, so it drops, and the ordered rule
   * drops everything after it.
   */
  scopeResolver(): ScopeResolver {
    return careerScopeResolver((key) => this.t(key))
  }

  /** The active left-pane section as a path segment — omitted at a stage's own root. */
  private areaSegment(): ScopeSegment[] {
    const items = this.sectionItems()
    const activeMatch = this.activeSectionMatch()
    const current = items.find((it) => it.match === activeMatch)
    if (!current || current.match === this.activeItem()?.match) return []
    return [
      {
        key: "area",
        id: areaIdOf(current.match),
        label: current.label,
        menuLabel: this.t("breadcrumb.switchSection"),
        choices: items.map((it) => ({ label: it.label, url: it.url, current: it.match === activeMatch })),
      },
    ]
  }

  /** The match prefix of the active destination — the shell's "surface" identity
   *  (drives the restrained dot-bounce on a surface switch; null when unresolved). */
  activeSpaceMatch(): string | null {
    return this.activeItem()?.match ?? null
  }

  themeMode(): ThemeMode {
    return this.theme.value
  }

  themeModes(): readonly ThemeMode[] {
    return THEME_MODES
  }

  themeLabel(mode: ThemeMode): CareerKey {
    return THEME_LABEL[mode]
  }

  go(url: string): void {
    void this.router.navigate(url)
  }

  /** Apply a theme mode: light/dark set <html data-theme>, auto removes it (follow OS). */
  setTheme(mode: ThemeMode): void {
    this.theme.set(mode)
    const root = globalThis.document?.documentElement
    if (root) {
      if (mode === "auto") root.removeAttribute("data-theme")
      else root.setAttribute("data-theme", mode)
    }
    try {
      globalThis.localStorage?.setItem(THEME_KEY, mode)
    } catch {
      // storage unavailable — the in-memory signal still drives this session.
    }
  }

  isNavCollapsed(): boolean {
    return this.navCollapsed.value
  }

  isDrawerOpen(): boolean {
    return this.navDrawerOpen.value
  }

  isAsideOpen(): boolean {
    return this.asideOpen.value
  }

  /** The far-left control: on MOBILE it opens/closes the overlay drawer; on DESKTOP it
   *  collapses/expands the inline pane to an icon rail (persisted). */
  toggleNav(): void {
    if (isMobileViewport()) {
      this.navDrawerOpen.set(!this.navDrawerOpen.value)
      return
    }
    const next = !this.navCollapsed.value
    this.navCollapsed.set(next)
    try {
      globalThis.localStorage?.setItem(NAV_KEY, next ? "1" : "0")
    } catch {
      // storage unavailable — session-only.
    }
  }

  /** Dismiss both mobile overlays (scrim tap / post-navigation) — desktop rail is untouched. */
  closePanes(): void {
    if (this.navDrawerOpen.value) this.navDrawerOpen.set(false)
    if (this.asideOpen.value) this.toggleAside()
  }

  /** Open/close the right AI-agent pane; persist the choice. */
  toggleAside(): void {
    const next = !this.asideOpen.value
    this.asideOpen.set(next)
    try {
      globalThis.localStorage?.setItem(ASIDE_KEY, next ? "1" : "0")
    } catch {
      // storage unavailable — session-only.
    }
  }

  /** The avatar glyph: a profile-image URL from the IdP, or initials to render as a fallback. */
  avatar(name: string): { picture?: string; initials: string } {
    const picture = this.session.user?.picture
    return { ...(picture ? { picture } : {}), initials: initialsOf(name) }
  }
}

export class CareerApp extends KitElement<ShellVm> {
  /** The destination the brand dot last acknowledged (null until the first render). */
  #lastSpaceMatch: string | null = null

  override connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener(SCOPE_SELECT_EVENT, this.#onScopeSelect)
  }

  override disconnectedCallback(): void {
    this.removeEventListener(SCOPE_SELECT_EVENT, this.#onScopeSelect)
    super.disconnectedCallback()
  }

  /**
   * The scope nav's ONE typed selection event (`@platform/web-kit`). A `url` choice is a
   * route change — cancelled so the anchor never reloads the app; an `id` choice is the
   * workshop-runner's step jump, which addresses a node in the CURRENT surface and changes
   * no route. The element closes its own dropdown either way.
   */
  readonly #onScopeSelect = (event: Event): void => {
    const { url, id, resolution } = (event as CustomEvent<ScopeSelectDetail>).detail
    event.preventDefault() // client-side navigation, no reload
    if (url !== undefined) {
      // The cascading rule already decided how much of the person's place survives
      // the switch: `resolution.url` is the DEEPEST crumb that still resolves (never
      // shallower than the chosen sibling), so it is the honest destination.
      this.vm.go(resolution?.url ?? url)
      // Navigating away resolves the "insufficient credits" prompt (the user has moved on).
      this.vm.dismissPaymentNotice()
      // On mobile, moving to a destination closes the overlay drawer behind it.
      if (this.vm.isDrawerOpen()) this.vm.closePanes()
      return
    }
    if (id === undefined) return
    globalThis.document
      ?.querySelector(`.timeline-node[urn="urn:career:workshop-step:${id}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  /**
   * I7: after each render — once the outlet has mounted the new surface — move focus to
   * <main id="main-content"> on a pending user navigation so keyboard/AT users continue INTO
   * the new content. The VM gates this to real navigations (never initial load / same-URL /
   * the workshop-runner's in-surface step jump, which is not a route change). `<main>` is
   * stable across the outlet swap, so focusing it never fights the surface's own render.
   *
   * Brand motion stays RESTRAINED in-app (§brand): the enter timeline plays once on mount
   * (`animated`); a destination switch gets only a subtle dot-bounce — deeper navigation
   * within a space animates nothing. Reduced motion short-circuits inside the element.
   */
  override flushRender(): void {
    super.flushRender()
    if (!this.isConnected) return
    if (this.vm.takePendingMainFocus()) {
      // Scrolling now lives INSIDE <main> (the shell frame doesn't scroll), so a route change
      // must reset THIS container's scrollTop — not window — or the new surface would inherit the
      // previous one's scroll offset. Reset first, then focus WITHOUT a scroll-jump.
      const main = this.querySelector<HTMLElement>("#main-content")
      if (main) {
        main.scrollTop = 0
        main.focus({ preventScroll: true })
      }
    }
    const space = this.vm.activeSpaceMatch()
    if (space !== null) {
      if (this.#lastSpaceMatch !== null && space !== this.#lastSpaceMatch) {
        void this.querySelector("kit-brand-mark")?.play("dot-bounce")
      }
      this.#lastSpaceMatch = space
    }
  }

  protected createViewModel(ctx: KitContext): ShellVm {
    return new ShellVm(
      ctx.inject(RouterToken),
      ctx.inject(I18nToken),
      ctx.inject(SessionToken),
      ctx.inject(IngestStoreToken),
      ctx.inject(BreadcrumbToken),
      ctx.inject(SectionNavToken),
    )
  }


  /**
   * The footer resource-path (§7) — the url-safe SLUG path to the asset in view, pinned right of
   * the pipeline spaces, shown only when the bar has room (CSS drops it first on narrow viewports).
   * Distinct from the human breadcrumb; renders slugs, not display-names. A display element now; the
   * future access-control disclosure hangs off this same seam.
   */
  private resourcePath(vm: ShellVm): TemplateResult {
    const segments = vm.resourcePathSegments()
    if (segments.length === 0) return nothing as unknown as TemplateResult
    const last = segments.length - 1
    return html`<div class="bar-respath" aria-label=${vm.t("bar.resourcePath.label")}>
      ${segments.map((seg, i) =>
        html`${i > 0 ? html`<span class="respath-sep" aria-hidden="true">/</span>` : nothing}<span
          class=${classMap({ "respath-seg": true, "respath-leaf": i === last })}
          >${seg}</span
        >`,
      )}
    </div>`
  }

  /**
   * The left-pane account block (native Popover) — the avatar (IdP picture or initials) + name +
   * email + chevron, and the popover that is the home for everything non-pipeline (§7a): Läufe,
   * KI-Einstellungen, Onboarding, Theme, Abmelden.
   */
  private avatarMenu(vm: ShellVm, name: string): TemplateResult {
    const { picture, initials } = vm.avatar(name)
    const email = vm.session.user?.email
    return html`
      <button type="button" class="shell-avatar" popovertarget=${USER_POP_ID} aria-label=${vm.t("user.menu.open")} lang="de">
        <span class="avatar-badge" aria-hidden="true">
          ${picture ? html`<img class="avatar-img" src=${picture} alt="" referrerpolicy="no-referrer" />` : html`<span class="avatar-initials">${initials}</span>`}
        </span>
        <span class="avatar-id">
          <span class="avatar-name" lang="de">${name}</span>
          ${email ? html`<span class="avatar-email">${email}</span>` : nothing}
        </span>
        <svg class="avatar-caret" viewBox="0 0 24 24" aria-hidden="true"><path d="m6 9 6 6 6-6" /></svg>
      </button>
      <div class="user-pop" popover="auto" id=${USER_POP_ID} lang="de">
        <p class="user-pop-head" lang="de">${vm.t("auth.signedInAs", { name })}</p>
        <!-- The non-pipeline destinations (Onboarding · Läufe · KI-Einstellungen …) live in the
             VISIBLE Konto block at the pane foot (D1); the popover keeps only identity chrome
             (theme + sign-out) so the two never drift out of sync. -->
        <div class="user-pop-theme" role="group" aria-label=${vm.t("theme.label")}>
          ${vm.themeModes().map(
            (mode) => html`<button
              type="button"
              data-act="set-theme"
              data-theme-set=${mode}
              aria-pressed=${vm.themeMode() === mode ? "true" : "false"}
              lang="de"
            >
              ${vm.t(vm.themeLabel(mode))}
            </button>`,
          )}
        </div>
        <a href="/api/auth/logout" class="user-pop-logout" lang="de">${vm.t("auth.logout")}</a>
      </div>
      <span id=${GATED_HINT_ID} class="visually-hidden" lang="de">${vm.t("gated.hint")}</span>
    `
  }

  /**
   * The Konto block pinned at the left pane's BOTTOM (D1): the labeled home for everything
   * NON-pipeline — Onboarding/Unterlagen · Läufe · KI-Einstellungen (gated cue) · Vorschläge
   * (once a run exists). Guthaben rides the balance chip below; user identity is the avatar.
   * This is what used to hide inside the Konto popover — now a visible, orienting group, and the
   * footer stays PURELY the four areas (no Konto entries there).
   */
  private kontoBlock(vm: ShellVm): TemplateResult {
    const items = vm.userMenuItems().filter((it) => it.url !== "/credits") // Guthaben = the balance chip below.
    return html`
      <nav class="konto-block" aria-label=${vm.t("sectionnav.account")}>
        <p class="konto-title" lang="de">${vm.t("sectionnav.account")}</p>
        ${items.map((item) => userMenuAnchor(vm, item))}
      </nav>
    `
  }

  /**
   * The credit-balance chip in the user area (foot of the left pane) — a small, theme-following
   * pill linking to /credits. Renders only once a balance is known (hidden while unknown or when
   * billing isn't live). Folds to the wallet glyph alone when the pane collapses to a rail.
   */
  private creditsChip(vm: ShellVm): TemplateResult | typeof nothing {
    const balance = vm.walletBalance()
    if (balance === null) return nothing
    return html`<a
      href="/credits"
      data-act="nav"
      data-url="/credits"
      class="credits-chip"
      aria-label=${vm.t("credits.chip.aria", { n: balance.toLocaleString("de-DE") })}
      title=${vm.t("credits.chip.aria", { n: balance.toLocaleString("de-DE") })}
      lang="de"
    >
      <svg class="credits-chip-icon" viewBox="0 0 24 24" aria-hidden="true">${GLYPH.wallet}</svg>
      <span class="credits-chip-value">${balance.toLocaleString("de-DE")}</span>
      <span class="credits-chip-label">${vm.t("credits.chip.label")}</span>
    </a>`
  }

  /**
   * The shared "insufficient credits" (402) notice — raised by the shared fetch layer for ANY
   * metered action, shown ONCE here (not per surface). An inline, dismissible banner with a link
   * to /credits. Never credits anything; it only points the user at the top-up surface.
   */
  private paymentNotice(vm: ShellVm): TemplateResult | typeof nothing {
    if (!vm.showPaymentNotice()) return nothing
    return html`
      <div class="pay-notice" role="alert">
        <svg class="pay-notice-icon" viewBox="0 0 24 24" aria-hidden="true">${GLYPH.wallet}</svg>
        <span class="pay-notice-text" lang="de">${vm.t(vm.paymentNoticeKey())}</span>
        <a class="pay-notice-cta" href="/credits" data-act="nav" data-url="/credits" lang="de">${vm.t("credits.notice.cta")}</a>
        <button
          type="button"
          class="pay-notice-dismiss"
          data-act="dismiss-payment-notice"
          aria-label=${vm.t("credits.notice.dismiss")}
          lang="de"
        >
          <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18" /></svg>
        </button>
      </div>
    `
  }

  /**
   * The session-expired re-auth prompt — raised by the shared fetch layer on ANY data-plane
   * 401 (the BFF already tried a transparent refresh, so re-auth is genuinely needed). It ASKS
   * rather than yanking the user to login: a calm line promising their progress is safe (surfaces
   * like the cardgame persist a local draft that survives the OIDC round-trip) plus a re-login
   * action. Not dismissible — the session really is dead; the only way forward is to sign in again.
   */
  private sessionExpiredNotice(vm: ShellVm): TemplateResult | typeof nothing {
    if (!vm.showSessionExpired()) return nothing
    return html`
      <div class="session-notice" role="alert">
        <svg class="session-notice-icon" viewBox="0 0 24 24" aria-hidden="true">${GLYPH.lock}</svg>
        <span class="session-notice-text" lang="de">${vm.t("session.expired.message")}</span>
        <button type="button" class="session-notice-cta" data-act="relogin" lang="de">${vm.t("session.expired.relogin")}</button>
      </div>
    `
  }

  /**
   * The right pane — the Platform AI Agent panel. It frames the plattform-wide agent and the
   * Platform Applications the user has access to (Career Studio being one; the list grows as
   * access is granted). The composer is an honest coming-soon placeholder — the concept is
   * represented correctly; wiring the real agent + full app roster is future work.
   */
  private agentPanel(vm: ShellVm): TemplateResult {
    return html`
      <div class="agent-head">
        <span class="agent-mark" aria-hidden="true">
          <svg viewBox="0 0 24 24"><path d="M12 3v4M12 17v4M3 12h4M17 12h4" /><circle cx="12" cy="12" r="4" /></svg>
        </span>
        <span class="agent-heading">
          <span class="agent-title" lang="de">${vm.t("ai.panel.title")}</span>
          <span class="agent-sub" lang="de">${vm.t("ai.panel.subtitle")}</span>
        </span>
      </div>
      <div class="agent-body">
        <p class="agent-section-label" lang="de">${vm.t("ai.panel.apps")}</p>
        <ul class="agent-apps" role="list">
          <li class="agent-app is-current">
            <span class="agent-app-dot" aria-hidden="true"></span>
            <span class="agent-app-name" lang="de">${vm.t("brand.name")}</span>
            <span class="agent-app-tag" lang="de">${vm.t("ai.panel.thisApp")}</span>
          </li>
        </ul>
        <p class="agent-more" lang="de">${vm.t("ai.panel.moreSoon")}</p>
      </div>
      <div class="agent-composer">
        <div class="agent-input" aria-hidden="true">${vm.t("ai.panel.composerPlaceholder")}</div>
        <span class="agent-soon" lang="de">${vm.t("ai.panel.comingSoon")}</span>
      </div>
    `
  }

  protected render(vm: ShellVm): TemplateResult {
    const { user } = vm.session
    const loginHref = `/api/auth/login?returnTo=${encodeURIComponent(vm.path)}`
    const scope = vm.scopeModel()
    const navCollapsed = vm.isNavCollapsed()
    // The Platform-AI right pane is a coming-soon stub — gate the whole feature (its
    // top-bar toggle AND the pane) behind dev mode, so real users never meet the stub.
    // The code path stays intact; it lights up again once devMode is on (heartbeat/dev
    // route precedent). Pane can only be open when it's shown.
    const showAside = vm.session.devMode
    const asideOpen = showAside && vm.isAsideOpen()
    const hasSections = vm.hasSections()
    const sections = vm.sectionItems()
    return html`
      <div
        class="shell"
        data-nav=${navCollapsed ? "collapsed" : "expanded"}
        data-drawer=${vm.isDrawerOpen() ? "open" : "closed"}
        data-aside=${asideOpen ? "open" : "closed"}
        data-sections=${hasSections ? "yes" : "no"}
      >
        <a class="skip-link" href="#main-content">${vm.t("a11y.skipToContent" as CareerKey)}</a>
        <!-- I7: a polite live region announces the destination surface on a route change
             (moves focus to #main-content too). Visually hidden; not focusable, so the
             skip-link stays first in the tab order. -->
        <p class="visually-hidden" aria-live="polite" aria-atomic="true" lang="de">${vm.announcementText()}</p>

        <header class="shell-head">
          <button
            type="button"
            class="chrome-toggle chrome-toggle-nav"
            data-act="toggle-nav"
            aria-label=${navCollapsed ? vm.t("nav.expandSidebar") : vm.t("nav.collapseSidebar")}
            aria-expanded=${navCollapsed ? "false" : "true"}
            lang="de"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">${GLYPH.sidebarLeft}</svg>
          </button>
          <!-- The ONE platform scope navigation (@platform/web-kit): the P-mark, then
               »Career« stacked above the current stage, then the deeper trail — every
               level with siblings a dropdown. The shell owns the MODEL (derived from
               routing in ShellVm.scopeModel()), the element owns the rendering; the
               chrome bar's dark tokens are handed to it as custom properties in the
               stylesheet, never as a palette inside the component. -->
          <kit-scope-nav
            class="shell-scope"
            .scope=${scope}
            .resolveScope=${vm.scopeResolver()}
            nav-label=${vm.t("breadcrumb.label")}
            content-lang="de"
          ></kit-scope-nav>
          ${showAside
            ? html`<button
                type="button"
                class="chrome-toggle chrome-toggle-aside"
                data-act="toggle-aside"
                aria-label=${vm.t("ai.panel.toggle")}
                aria-expanded=${asideOpen ? "true" : "false"}
                aria-pressed=${asideOpen ? "true" : "false"}
                lang="de"
              >
                <svg viewBox="0 0 24 24" aria-hidden="true">${GLYPH.sidebarRight}</svg>
              </button>`
            : nothing}
        </header>

        <nav class="shell-nav" aria-label=${vm.t("sectionnav.label")}>
          <div class="nav-space-head" aria-hidden=${navCollapsed ? "true" : "false"}>
            <span class="nav-space-icon">${sectionIconGlyph(vm.sectionTitleIcon())}</span>
            <span class="nav-space-title" lang="de">${vm.sectionTitle()}</span>
          </div>
          <div class="nav-group">
            ${hasSections
              ? sections.map((item) => sectionAnchor(vm, item))
              : html`<p class="nav-empty" lang="de">${vm.t("sectionnav.empty")}</p>`}
          </div>
          <div class="nav-foot">
            ${user ? this.kontoBlock(vm) : nothing}
            ${user ? this.creditsChip(vm) : nothing}
            ${user
              ? this.avatarMenu(vm, user.name ?? user.sub)
              : html`<a href=${loginHref} class="nav-item nav-signin" lang="de">${navIcon("nav.profil")}<span class="nav-item-label">${vm.t("auth.login")}</span></a>`}
          </div>
        </nav>

        <main id="main-content" tabindex="-1" class="shell-main">
          <div class="main-inner">
            ${this.sessionExpiredNotice(vm)}
            ${this.paymentNotice(vm)}
            <kit-outlet></kit-outlet>
          </div>
        </main>

        <!-- The LLM human-review pane (§9.2). Mounted always, renders NOTHING unless a call of
             this user's own is paused — and it is only ever armed for a caller who switched it on. -->
        <career-llm-review></career-llm-review>

        ${showAside
          ? html`<aside class="shell-aside" aria-label=${vm.t("ai.panel.title")} aria-hidden=${asideOpen ? "false" : "true"}>
              ${this.agentPanel(vm)}
            </aside>`
          : nothing}

        <footer class="shell-foot">
          <nav class="foot-nav" aria-label=${vm.t("bar.nav.label")}>
            ${vm.primaryItems().map((item) => footAnchor(vm, item))}
          </nav>
          ${this.resourcePath(vm)}
        </footer>

        <!-- Mobile scrim — taps close whichever pane is open (left drawer or AI panel). -->
        <button
          type="button"
          class="shell-scrim"
          data-act="close-panes"
          aria-label=${vm.t("nav.collapseSidebar")}
          tabindex="-1"
        ></button>
      </div>
    `
  }

  protected override actions(vm: ShellVm): ActionMap {
    return {
      nav: ({ url }, event) => {
        event.preventDefault() // client-side navigation, no reload
        // Dismiss any open menu the click came from (user-menu popover or a crumb dropdown).
        const target = event.target as Element | null
        ;(target?.closest?.("[popover]") as HTMLElement | null)?.hidePopover?.()
        const det = target?.closest?.("details[open]") as HTMLDetailsElement | null
        if (det) det.open = false
        // In-page document anchor (a surface-contributed section OUTLINE, e.g. the Kompass
        // profile asset — §7a): a `#id` target scrolls that section into view instead of
        // routing. Surfaces render into light DOM, so the id resolves at document level.
        if (url?.startsWith("#")) {
          globalThis.document?.getElementById(url.slice(1))?.scrollIntoView({ behavior: "smooth", block: "start" })
          if (vm.isDrawerOpen()) vm.closePanes()
          return
        }
        if (url) vm.go(url)
        // Navigating away resolves the "insufficient credits" prompt (the user has moved on / is topping up).
        vm.dismissPaymentNotice()
        // On mobile, moving to a destination closes the overlay drawer behind it.
        if (vm.isDrawerOpen()) vm.closePanes()
      },
      "set-theme": ({ themeSet }) => {
        if (themeSet === "light" || themeSet === "dark" || themeSet === "auto") vm.setTheme(themeSet)
      },
      "toggle-nav": () => vm.toggleNav(),
      "toggle-aside": () => vm.toggleAside(),
      // The scrim shows on mobile only when a pane is open; a tap dismisses both overlays.
      "close-panes": () => vm.closePanes(),
      "dismiss-payment-notice": () => vm.dismissPaymentNotice(),
      relogin: () => vm.relogin(),
    }
  }
}
